// ── ORA.3b Org Risk Acceptance hooks ──────────────────────────────────────────
//
// All ORA.3b hooks consolidated in one file per cluster single-file hook pattern.
// Parallel to useRiskAcceptances.ts (vendor RA); D-ORA-ARCH-9 separation maintained.
// React Query queryKeys include tenantId for cache isolation.
//
// Endpoint inventory:
//   GET  /org-risk-acceptances/:id      — useOrgRiskAcceptance
//   GET  /org-risk-acceptances          — useOrgRiskAcceptances / useDashboardOrgRisks
//   POST /org-risk-acceptances          — useCreateOrgRiskAcceptance (createAndSubmit)
//   POST /org-risk-acceptances/draft    — useCreateOrgRiskAcceptance (createDraft path)
//   PATCH /org-risk-acceptances/:id     — useUpdateDraftOrgRiskAcceptance
//   POST /org-risk-acceptances/:id/submit       — useSubmitOrgRiskAcceptance
//   POST /org-risk-acceptances/:id/resume-draft — useResumeOrgDraft
//   POST /org-risk-acceptances/:id/{approve|deny|return} — useOrgRiskAcceptanceAction

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type {
  OraEntry,
  OraListResult,
  OraListFilters,
  OraOverview,
  CreateOraAndSubmitPayload,
  UpdateOraPayload,
  ApproveOraPayload,
  DenyOraPayload,
  ReturnOraPayload,
  CloseAsMitigatedOraPayload,
  RevokeOraPayload,
  RequestOrgRenewalPayload,
} from '@/api/types/org-risk-acceptance.types';

// ── RR-BI-1 Slice 2b: useOraOverview — GET /org-risk-acceptances/overview (operational BI aggregate) ──

export function useOraOverview() {
  const { tenantId, isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: ['org-risk-acceptances-overview', tenantId],
    queryFn: async (): Promise<OraOverview> => {
      const { data } = await apiClient.get<{ data: OraOverview }>(`/org-risk-acceptances/overview`);
      return data.data;
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

// ── useOrgRiskAcceptance — GET /org-risk-acceptances/:id ─────────────────────

export function useOrgRiskAcceptance(id: string | undefined) {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['org-risk-acceptance', tenantId, id],
    queryFn: async (): Promise<OraEntry> => {
      const { data } = await apiClient.get<{ data: OraEntry }>(
        `/org-risk-acceptances/${id}`,
      );
      if (!data.data) throw new Error('Org risk response not found');
      return data.data;
    },
    enabled: isAuthenticated && !!id,
    staleTime: 60_000,
    refetchOnMount: 'always',
  });
}

// ── useOrgRiskAcceptances — GET /org-risk-acceptances (list with filters) ─────

export function useOrgRiskAcceptances(filters: OraListFilters = {}) {
  const { tenantId, isAuthenticated } = useAuthStore();

  const params = new URLSearchParams();
  // ⚠ O4a reads-flip: native lifecycle filters (was ?state=)
  if (filters.workflow_status) params.set('workflow_status', filters.workflow_status);
  if (filters.closure_reason) params.set('closure_reason', filters.closure_reason);
  // ⚠ RR-BI-1 Slice 1: free-text search (on-demand)
  if (filters.search) params.set('search', filters.search);
  if (filters.requires_action_by_user != null) {
    params.set('requires_action_by_user', String(filters.requires_action_by_user));
  }
  if (filters.approaching_expiry != null) {
    params.set('approaching_expiry', String(filters.approaching_expiry));
  }
  if (filters.has_renewals != null) {
    params.set('has_renewals', String(filters.has_renewals));
  }
  if (filters.risk_category) params.set('risk_category', filters.risk_category);
  params.set('limit', String(filters.limit ?? 25));
  params.set('offset', String(filters.offset ?? 0));
  params.set('sort_by', filters.sort_by ?? 'updated_at');
  params.set('sort_order', filters.sort_order ?? 'desc');

  return useQuery({
    queryKey: ['org-risk-acceptances', tenantId, filters],
    queryFn: async (): Promise<OraListResult> => {
      const { data } = await apiClient.get<{ data: OraListResult }>(
        `/org-risk-acceptances?${params.toString()}`,
      );
      return data.data ?? { items: [], total: 0, limit: filters.limit ?? 25, offset: filters.offset ?? 0 };
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

// ── useDashboardOrgRisks — GET /org-risk-acceptances?requires_action_by_user=true&limit=5 ──
//
// Powers "Organizational Risks Requiring Attention" dashboard tile.
// Fetches up to 5 entries requiring current user's action (approver + submitter buckets).

export function useDashboardOrgRisks() {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['dashboard-org-risks', tenantId],
    queryFn: async (): Promise<OraEntry[]> => {
      const { data } = await apiClient.get<{ data: OraListResult }>(
        `/org-risk-acceptances?workflow_status=pending_approval,draft,returned&requires_action_by_user=true&limit=5&sort_by=updated_at&sort_order=desc`,
      );
      return data.data?.items ?? [];
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

// ── useCreateOrgRiskAcceptance — POST /org-risk-acceptances (createAndSubmit) ─
// or POST /org-risk-acceptances/draft (createDraft)
//
// Supports both create paths via a `mode` field:
//   mode='submit' → POST / (createAndSubmit — direct to pending_approval)
//   mode='draft'  → POST /draft (createDraft — save as draft)

export function useCreateOrgRiskAcceptance() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (dto: CreateOraAndSubmitPayload & { mode: 'draft' | 'submit' }): Promise<{ data: OraEntry }> => {
      const { mode, ...payload } = dto;
      const url = mode === 'draft' ? '/org-risk-acceptances/draft' : '/org-risk-acceptances';
      const res = await apiClient.post<{ data: OraEntry; errors: unknown }>(url, payload);
      return { data: res.data.data };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-risk-acceptances', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard-org-risks', tenantId] });
    },
  });
}

// ── useUpdateDraftOrgRiskAcceptance — PATCH /org-risk-acceptances/:id ─────────

export function useUpdateDraftOrgRiskAcceptance() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateOraPayload }): Promise<OraEntry> => {
      const { data } = await apiClient.patch<{ data: OraEntry }>(
        `/org-risk-acceptances/${id}`,
        payload,
      );
      return data.data;
    },
    onSuccess: (entry) => {
      qc.invalidateQueries({ queryKey: ['org-risk-acceptance', tenantId, entry.id] });
      qc.invalidateQueries({ queryKey: ['org-risk-acceptances', tenantId] });
    },
  });
}

// ── useSubmitOrgRiskAcceptance — POST /org-risk-acceptances/:id/submit ─────────

export function useSubmitOrgRiskAcceptance() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string): Promise<OraEntry> => {
      const { data } = await apiClient.post<{ data: OraEntry }>(
        `/org-risk-acceptances/${id}/submit`,
        {},
      );
      return data.data;
    },
    onSuccess: (entry) => {
      qc.invalidateQueries({ queryKey: ['org-risk-acceptance', tenantId, entry.id] });
      qc.invalidateQueries({ queryKey: ['org-risk-acceptances', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard-org-risks', tenantId] });
    },
  });
}

// ── useResumeOrgDraft — POST /org-risk-acceptances/:id/resume-draft ────────────
//
// RETURNED → DRAFT. Used from ORA detail page "Resume Draft" button.
// On success: caller routes to /org-risk-acceptances/new?from=:id for pre-populated edit.

export function useResumeOrgDraft() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string): Promise<OraEntry> => {
      const { data } = await apiClient.post<{ data: OraEntry }>(
        `/org-risk-acceptances/${id}/resume-draft`,
        {},
      );
      return data.data;
    },
    onSuccess: (entry) => {
      qc.invalidateQueries({ queryKey: ['org-risk-acceptance', tenantId, entry.id] });
      qc.invalidateQueries({ queryKey: ['org-risk-acceptances', tenantId] });
    },
  });
}

// ── useOrgRiskAcceptanceAction — POST /org-risk-acceptances/:id/{action} ───────
//
// Generic mutation for Approver actions on ORA entries.
// ORA.5 A3: OraActionType expanded from 3 to 7 (close-as-mitigated, revoke,
// approve-renewal, deny-renewal added; dead comments removed per cleanup).

export type OraActionType =
  | 'approve'
  | 'deny'
  | 'return'
  | 'close-as-mitigated'
  | 'revoke'
  | 'approve-renewal'
  | 'deny-renewal';

export type OraActionPayload =
  | ApproveOraPayload
  | DenyOraPayload
  | ReturnOraPayload
  | CloseAsMitigatedOraPayload
  | RevokeOraPayload;

function oraActionToEndpoint(action: OraActionType): string {
  // approve-renewal / deny-renewal POST to the same /:id/approve or /:id/deny
  // endpoints; the server distinguishes by parent_acceptance_id on the entry.
  if (action === 'approve-renewal') return 'approve';
  if (action === 'deny-renewal')    return 'deny';
  return action;
}

export function useOrgRiskAcceptanceAction(action: OraActionType) {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: OraActionPayload | Record<string, string> }): Promise<OraEntry> => {
      const { data } = await apiClient.post<{ data: OraEntry }>(
        `/org-risk-acceptances/${id}/${oraActionToEndpoint(action)}`,
        payload,
      );
      return data.data;
    },
    onSuccess: (entry) => {
      qc.invalidateQueries({ queryKey: ['org-risk-acceptance', tenantId, entry.id] });
      qc.invalidateQueries({ queryKey: ['org-risk-acceptances', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard-org-risks', tenantId] });
      toast.success(
        action === 'approve'           ? 'Org risk response approved' :
        action === 'deny'              ? 'Org risk response denied' :
        action === 'return'            ? 'Returned for revision' :
        action === 'close-as-mitigated'? 'Closed as mitigated' :
        action === 'revoke'            ? 'Approval revoked' :
        action === 'approve-renewal'   ? 'Renewal approved' :
        'Renewal denied',
      );
    },
  });
}

// ── useApproveOrgRenewal / useDenyOrgRenewal — renewal approval shortcuts ──────

export function useApproveOrgRenewal() {
  return useOrgRiskAcceptanceAction('approve-renewal');
}

export function useDenyOrgRenewal() {
  return useOrgRiskAcceptanceAction('deny-renewal');
}

// ── useRequestOrgRenewal — POST /org-risk-acceptances/:id/request-renewal ──────
//
// ORA.5 A6: standalone hook (mirrors vendor useRequestRenewal pattern).
// Submitter-only; creates child entry (RENEWAL_REQUESTED event type).
// No toast on success: caller navigates to child detail page.

export function useRequestOrgRenewal() {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async ({
      parentId,
      payload,
    }: {
      parentId: string;
      payload: RequestOrgRenewalPayload;
    }): Promise<OraEntry> => {
      const { data } = await apiClient.post<{ data: OraEntry }>(
        `/org-risk-acceptances/${parentId}/request-renewal`,
        payload,
      );
      return data.data;
    },
    onSuccess: (entry) => {
      qc.invalidateQueries({ queryKey: ['org-risk-acceptance', tenantId, entry.id] });
      qc.invalidateQueries({ queryKey: ['org-risk-acceptances', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard-org-risks', tenantId] });
      if (entry.parent_acceptance_id) {
        qc.invalidateQueries({
          queryKey: ['org-risk-acceptance', tenantId, entry.parent_acceptance_id],
        });
      }
    },
  });
}
